import React, { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Checkbox,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import {
  ModalBaseStyles,
  ModalActionButtonStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { debounce } from "lodash";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { CohortPreviewResult } from "./types";

interface AddToStudyModalProps {
  open: boolean;
  onClose: () => void;
  selectedParticipants: Set<string>;
  cohortData: CohortPreviewResult | undefined;
}

const AddToStudyModal: React.FC<AddToStudyModalProps> = ({
  open,
  onClose,
  selectedParticipants,
  cohortData,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [loader, setloader] = useState(false);
  const [sendEmailInvite, setSendEmailInvite] = useState(true);
  const [studies, setStudies] = useState<Array<{ id: string; name: string }>>(
    []
  );

  const handleSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          if (value?.length >= 3) {
            setloader(true);
            const res: AxiosResponse = await http.get(
              `/platform/study?page=1&size=15&query=${value}`
            );
            const studyList = res.data.data.map((study: any) => {
              return {
                id: study?.id,
                name: study?.name,
              };
            });

            setStudies(studyList);
          }
        } catch (err) {
          errorToastMessage(err as Error);
        } finally {
          setloader(false);
        }
      }, 500),
    []
  );

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    try {
      if (!selectedStudy) {
        toastMessage("warning", "Please select a study before proceeding");
        return;
      }
      setSubmitLoading(true);
      const body = {
        studyId: selectedStudy.id,
        participantIds: Array.from(selectedParticipants),
        sendEmail: sendEmailInvite,
      };
      const res: AxiosResponse = await http.post(
        `/admin/registry/cohort-query/${cohortData?.cohortQueryId}/runs/${cohortData?.cohortRunId}/enroll`,
        body
      );
      toastMessage("success", res.data.message);
      handleClose();
    } catch (err) {
      setSubmitLoading(false);
      errorToastMessage(err as Error);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "20vh" }}>
        <ModalHeader
          title="Add Participants to Study"
          onCloseClick={handleClose}
        />

        <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
          Selected Participants: {selectedParticipants.size}
        </Typography>

        <Autocomplete
          id="study-selection"
          filterOptions={(x) => x}
          onInputChange={(_1: any, value: any, reason: string) => {
            if (reason === "input") handleSearch(value);
          }}
          onChange={(_1: any, value: any) => {
            setSelectedStudy(value || null);
          }}
          options={studies}
          value={selectedStudy || null}
          isOptionEqualToValue={(option, value) => {
            return option.id === value.id;
          }}
          loading={loader}
          loadingText={<CircularProgress size={20} />}
          noOptionsText="No Results"
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          clearOnBlur={true}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedStudy ? undefined : "Choose a study"}
              sx={{ backgroundColor: "white", borderRadius: "6px" }}
              helperText={
                selectedStudy ? undefined : "Enter atleast 3 characters"
              }
            />
          )}
        />

        <FormControl sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailInvite}
                onChange={(event) => {
                  setSendEmailInvite(event.target.checked);
                }}
              />
            }
            label="Send email invite"
          />
        </FormControl>

        {/* Action Buttons */}
        <Box sx={{ ...ModalActionButtonStyles, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={submitLoading}
            data-testid="button-cancel-save"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="contained"
            disabled={submitLoading}
            onClick={handleSave}
            data-testid="button-confirm-save"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {submitLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Add...
              </Box>
            ) : (
              "Add to Study"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddToStudyModal;
